import { PrimaryButton } from "@digipost/dds-buttons-react";
import { DelayedSpinner } from "@digipost/dds-spinner-react";
import * as Sentry from "@sentry/react";
import { PropsWithChildren, useEffect, useState } from "react";

import ErrorPage from "../ErrorPage";
import Header from "../header/Header";
import { findURIWithWindowHostname, linkExist } from "./linkUtils";
import { logError } from "./serverlog";
import Translated from "./Translated";
import { WebApiError } from "./web-api-error";

const AppErrorBoundary: React.FC<PropsWithChildren> = ({ children }) => {
  const [error, setError] = useState<unknown>();

  const isNotAuthorizedErrorWithLogoutLink =
    error instanceof WebApiError &&
    error.statusCode === 403 &&
    error.errorCode === "NOT_AUTHORIZED" &&
    linkExist("logout_idp", error.link);

  const isNotAuthorizedErrorWithLoginLink =
    error instanceof WebApiError &&
    error.statusCode === 403 &&
    error.errorCode === "NOT_AUTHORIZED" &&
    linkExist("login_idp", error.link);

  useEffect(() => {
    if (isNotAuthorizedErrorWithLoginLink) {
      const loginIdpUrl = findURIWithWindowHostname(
        "login_idp",
        error!.link ?? [],
      );

      const returnUrl =
        "/registrering/person/not-registered" + window.location.search;
      const returnUrlError = "/registrering/" + window.location.search;

      window.location.href =
        loginIdpUrl +
        `?provider=idporten&eidasLoa=HIGH&rolle=REGISTRERING&returnUrl=${returnUrl}&returnUrlError=${returnUrlError}`;
    }
  }, [isNotAuthorizedErrorWithLoginLink]);

  if (isNotAuthorizedErrorWithLoginLink) {
    return <DelayedSpinner />;
  }

  return (
    <Sentry.ErrorBoundary
      onError={(error, _componentStack, sentryEventId) => {
        logError(error, { origin: "AppErrorBoundary", sentryEventId });
        setError(error);
      }}
      fallback={
        <>
          <Header />
          <ErrorPage>
            {isNotAuthorizedErrorWithLogoutLink ? (
              <PrimaryButton
                as="a"
                href={findURIWithWindowHostname("logout_idp", error.link)}
              >
                <Translated textKey="generic.logout" />
              </PrimaryButton>
            ) : (
              <>
                <Translated textKey="error.app.try_again" />
                <PrimaryButton onClick={() => window.location.reload()}>
                  <Translated textKey="generic.retry" />
                </PrimaryButton>
              </>
            )}
          </ErrorPage>
        </>
      }
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default Sentry.withErrorBoundary(AppErrorBoundary, {});
